import { isPlatformBrowser } from '@angular/common';
import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CoiService } from './coi';
import { ToastService } from './toast';

/**
 * Fetches Certificate-of-Insurance bytes/HTML through HttpClient (so the auth interceptor attaches
 * the JWT) and hands them to the browser. The COI preview uses the SERVER HTML (the fixed Generali
 * layout) rendered in an iframe via srcdoc — never re-implemented in Angular — so it always matches
 * the PDF. Every method is SSR-safe (no-ops on the server) and surfaces failures via the toast.
 */
@Injectable({ providedIn: 'root' })
export class CoiDownloader {
  private platformId = inject(PLATFORM_ID);
  private svc = inject(CoiService);
  private toast = inject(ToastService);

  /** Fetch the certificate HTML for an iframe srcdoc preview. Returns '' on failure (toasts why). */
  async loadCoiHtml(memberId: number): Promise<string> {
    if (!isPlatformBrowser(this.platformId)) return '';
    try {
      return await firstValueFrom(this.svc.coiHtml(memberId));
    } catch (err) {
      this.toast.error(
        (await this.readBlobError(err)) || 'Certificate is available only after the policy is issued.',
      );
      return '';
    }
  }

  /** Download the PDF certificate. `coiOnly` = certificate alone (no welcome letter prepended).
   *  Falls back to opening the HTML for print on an unexpected 500. */
  async downloadCoi(memberId: number, certNo?: string | number, coiOnly = false): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      const blob = await firstValueFrom(this.svc.coiPdf(memberId, coiOnly));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const prefix = coiOnly ? 'COI' : 'WelcomeLetter-COI';
      a.download = `${prefix}-${(certNo ?? memberId).toString().replace(/[^\w-]/g, '')}.pdf`;
      document.body.appendChild(a);
      a.click();
      // Defer cleanup off the click tick — a synchronous revoke can abort the download in some browsers.
      setTimeout(() => {
        a.remove();
        URL.revokeObjectURL(url);
      }, 0);
    } catch (err: any) {
      this.toast.error((await this.readBlobError(err)) || 'Could not generate the certificate PDF.');
      // Defensive fallback ONLY for an unexpected 500 (PDF render failed but the HTML is still
      // serviceable). On a 400 "not issued" / 404 / 401 the HTML endpoint fails identically, so
      // falling back there would just double-toast (and pop a pointless print window).
      if (err?.status === 500) await this.printCoi(memberId);
    }
  }

  /** Download the WELCOME LETTER only (partner-onboarded, issued members). */
  async downloadWelcome(memberId: number, certNo?: string | number): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      const blob = await firstValueFrom(this.svc.welcomePdf(memberId));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `WelcomeLetter-${(certNo ?? memberId).toString().replace(/[^\w-]/g, '')}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        a.remove();
        URL.revokeObjectURL(url);
      }, 0);
    } catch (err: any) {
      this.toast.error(
        (await this.readBlobError(err)) || 'No welcome letter is available for this member.',
      );
    }
  }

  /** Open the certificate HTML in a new window and trigger the print dialog (Print → Save as PDF). */
  async printCoi(memberId: number): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    const html = await this.loadCoiHtml(memberId);
    if (!html) return; // loadCoiHtml already toasted the reason
    const w = window.open('', '_blank', 'width=900,height=1000');
    if (!w) {
      this.toast.error('Allow pop-ups to print the certificate.');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.onload = () => {
      w.focus();
      w.print();
    };
  }

  /** Blob/text error bodies arrive as a Blob (blob requests) or a string (text requests) — both
   *  carry the JSON envelope, so parse either for `.message`. */
  private async readBlobError(err: any): Promise<string> {
    try {
      const body = err?.error;
      if (body instanceof Blob) {
        const text = await body.text();
        try {
          return JSON.parse(text)?.message ?? text;
        } catch {
          return text;
        }
      }
      if (typeof body === 'string') {
        try {
          return JSON.parse(body)?.message ?? body;
        } catch {
          return body;
        }
      }
      return body?.message ?? err?.message ?? '';
    } catch {
      return '';
    }
  }
}
